import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Async Thunks for Process CRUD operations

// Get all processes
export const getAllProcesses = createAsyncThunk(
  "process/getAllProcesses",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/processes");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch processes"
      );
    }
  }
);

// Get my processes (created by logged-in user)
export const getMyProcesses = createAsyncThunk(
  "process/getMyProcesses",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/processes/my");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch your processes"
      );
    }
  }
);

// Get single process by ID
export const getProcessById = createAsyncThunk(
  "process/getProcessById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/process/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch process"
      );
    }
  }
);

// Create new process
export const createProcess = createAsyncThunk(
  "process/createProcess",
  async (processData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/process", processData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to create process"
      );
    }
  }
);

// Update process
export const updateProcess = createAsyncThunk(
  "process/updateProcess",
  async ({ id, processData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/process/${id}`, processData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to update process"
      );
    }
  }
);

// Delete process
export const deleteProcess = createAsyncThunk(
  "process/deleteProcess",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/process/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete process"
      );
    }
  }
);

// Search processes
export const searchProcesses = createAsyncThunk(
  "process/searchProcesses",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/processes/search?search=${searchQuery}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to search processes"
      );
    }
  }
);

// Get processes with pagination
export const getProcessesWithPagination = createAsyncThunk(
  "process/getProcessesWithPagination",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/processes/paginated?page=${page}&limit=${limit}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch processes"
      );
    }
  }
);

// Process Slice
const processSlice = createSlice({
  name: "process",
  initialState: {
    processes: [],
    currentProcess: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    setCurrentProcess: (state, action) => {
      state.currentProcess = action.payload;
    },
    clearCurrentProcess: (state) => {
      state.currentProcess = null;
    },
  },
  extraReducers: (builder) => {
    // Get all processes
    builder.addCase(getAllProcesses.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllProcesses.fulfilled, (state, action) => {
      state.loading = false;
      state.processes = action.payload;
      state.error = null;
    });
    builder.addCase(getAllProcesses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get my processes
    builder.addCase(getMyProcesses.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyProcesses.fulfilled, (state, action) => {
      state.loading = false;
      state.processes = action.payload;
      state.error = null;
    });
    builder.addCase(getMyProcesses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get process by ID
    builder.addCase(getProcessById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getProcessById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentProcess = action.payload;
      state.error = null;
    });
    builder.addCase(getProcessById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create process
    builder.addCase(createProcess.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createProcess.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.processes.unshift(action.payload);
      state.message = "Process created successfully";
      state.error = null;
    });
    builder.addCase(createProcess.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update process
    builder.addCase(updateProcess.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateProcess.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Process updated successfully";
      state.processes = state.processes.map((process) =>
        process._id === action.payload._id ? action.payload : process
      );
      if (state.currentProcess?._id === action.payload._id) {
        state.currentProcess = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateProcess.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete process
    builder.addCase(deleteProcess.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteProcess.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Process deleted successfully";
      state.processes = state.processes.filter(
        (process) => process._id !== action.payload
      );
      if (state.currentProcess?._id === action.payload) {
        state.currentProcess = null;
      }
      state.error = null;
    });
    builder.addCase(deleteProcess.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Search processes
    builder.addCase(searchProcesses.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchProcesses.fulfilled, (state, action) => {
      state.loading = false;
      state.processes = action.payload;
      state.error = null;
    });
    builder.addCase(searchProcesses.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get processes with pagination
    builder.addCase(getProcessesWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getProcessesWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      state.processes = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit || 10,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.error = null;
    });
    builder.addCase(getProcessesWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const {
  clearErrors,
  clearMessage,
  resetSuccess,
  setCurrentProcess,
  clearCurrentProcess,
} = processSlice.actions;

export default processSlice.reducer;

